import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import "@26lights/orcha/dist/orcha.css";
import {
  GET_BASIC_PROJECT,
  GET_PROJECT_USERS,
} from "@/graphql/project/queries";
import { GET_PHASES } from "@/graphql/orcha/phases/queries";
import { GET_MILESTONES_FOR_PROJECTS } from "@/graphql/orcha/milestones/queries";
import {
  ARCHIVE_PHASE,
  BULK_CREATE_PHASES,
  UNARCHIVE_PHASE,
  CREATE_PHASE,
  UPDATE_PHASE,
} from "@/graphql/orcha/phases/mutations";
import {
  ARCHIVE_MILESTONE,
  BULK_CREATE_MILESTONES,
  CREATE_MILESTONE,
  UNARCHIVE_MILESTONE,
  UPDATE_MILESTONE,
} from "@/graphql/orcha/milestones/mutations";
import {
  ARCHIVE_ACTIVITY,
  BULK_CREATE_ACTIVITIES,
  CREATE_ACTIVITY,
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
  UNARCHIVE_ACTIVITY,
  UPDATE_ACTIVITY,
} from "@/graphql/orcha/activities/mutations";
import { CREATE_SPRINT } from "@/graphql/orcha/sprints/mutations";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useLazyQuery,
} from "@vue/apollo-composable";
import { useRoute, useRouter } from "vue-router";
import projectOverviewI18n from "@/locales/projectOverview/en-UK.json";
import activityFeedI18n from "@/locales/activityFeed/en-UK.json";
import { useI18n } from "vue-i18n";
import { GET_USER } from "@/graphql/user/queries";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { GET_ACTIVITY_TIME_SPENT } from "@/graphql/timesheets/queries";
import {
  AddFilesPayload,
  Attachment,
  feedFilterTypes,
  RemoveFilePayload,
} from "@26lights/orcha";
import {
  CREATE_TASK,
  DELETE_TASK,
  UPDATE_TASK,
} from "@/graphql/orcha/tasks/mutations";
import {
  CREATE_COMMENT,
  DELETE_COMMENT,
  ON_NOTIFY_MENTIONED_USERS,
  UPDATE_COMMENT,
} from "@/graphql/orcha/comments/mutations";
import {
  APPLY_TEMPLATE,
  SAVE_TEMPLATE,
} from "@/graphql/orcha/templates/mutations";
import { GET_TEMPLATES_NAMES } from "@/graphql/orcha/templates/queries";
import {
  GET_FULL_ACTIVITIES,
  GET_ACTIVITY_STATUSES,
  GET_ACTIVITY,
  GET_ACTIVITY_DEV_STATUSES,
} from "@/graphql/orcha/activities/queries";
import { GET_SPRINTS } from "@/graphql/orcha/sprints/queries";
import { GET_PLANNED_MAN_DAYS_FOR_SPRINTS } from "@/graphql/planning/queries";
import { GET_HISTORY_EVENTS } from "@/graphql/auditConfig/queries";
import useMutationHandlers from "@/composables/useMutationHandlers";
import { getFileBlob, uploadFile } from "@/services/file";
import { useToast } from "vue-toastification";
import { logErrorMessages } from "@vue/apollo-util";
import { isEmployee } from "@/helpers/router/queries";
import {
  FeedFiltersSearch,
  ProjectAccessRight,
} from "@/helpers/typesDefinition";
import _ from "lodash";
import { updateQueryCache } from "@/helpers/cache/cache";
import ActivityTasksCacheInfoHolder from "@/helpers/cache/ActivityTasksCacheInfoHolder";
import { insertNewEntity } from "@/helpers/cache/insertNewEntity";
import { removeEntity } from "@/helpers/cache/removeEntity";
import ActivityCommentsCacheInfoHolder from "@/helpers/cache/ActivityCommentsCacheInfoHolder";
import ActivityAttachmentsCacheInfoHolder from "@/helpers/cache/ActivityAttachmentsCacheInfoHolder";
import { User } from "@/helpers/user/typesDefinition";
import {
  AddCommentPayload,
  BulkAddActivityPayload,
  BulkAddMilestonePayload,
  BulkAddPhasePayload,
  UpdateCommentPayload,
} from "@26lights/orcha/dist/src/types";

type MessageSchema = typeof projectOverviewI18n & typeof activityFeedI18n;


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectOverview',
  setup(__props) {

const toast = useToast();

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": _.merge(projectOverviewI18n, activityFeedI18n),
  },
});

const route = useRoute();
const router = useRouter();

const filters = ref<FeedFiltersSearch>({
  searchTerm: "",
  [feedFilterTypes.project]: route.params.id ? [route.params.id as string] : [],
  [feedFilterTypes.sprint]: [],
  [feedFilterTypes.assignee]: [],
  [feedFilterTypes.milestone]: [],
  includesInactive: false,
  onlyImportant: false,
  onlyNonAssigned: false,
});

const { createErrorHandler, createResultHandler } = useMutationHandlers();

/*
 * Project
 */
const { result: projectResult, onError: onProjectError } = useQuery(
  GET_BASIC_PROJECT,
  {
    id: route.params.id,
  },
);

onProjectError(createErrorHandler("getProject"));

/*
 * Current user
 */
const { result: userResult, onError: onUserError } = useQuery(GET_USER);

onUserError(createErrorHandler("getUser"));

const isUserEmployee = computed(() =>
  isEmployee(userResult.value?.user?.accessRights),
);

/*
 * Activities
 */
const { result: activitiesResult, onError: onActivitiesError } = useQuery(
  GET_FULL_ACTIVITIES,
  filters,
);

const activities = computed(() => activitiesResult.value?.activities ?? []);

onActivitiesError(createErrorHandler("getActivities"));

/*
 * Sprints
 */
const {
  result: sprintsResult,
  onError: onSprintsError,
  refetch: refetchSprints,
} = useQuery(
  GET_SPRINTS,
  () => ({
    projectId: route.params.id as string,
  }),
  () => ({
    enabled: Boolean(route.params.id),
  }),
);

const sprints = computed(() => sprintsResult.value?.sprints ?? []);

onSprintsError(createErrorHandler("getSprints"));

// Get selected sprint IDs from filter
const selectedSprintIds = computed(() => {
  return filters.value[feedFilterTypes.sprint] ?? [];
});

// Build sprint configs for planning query
const sprintConfigs = computed(() => {
  return selectedSprintIds.value
    .map((id) => {
      const sprint = sprints.value.find((s: { id: string }) => s.id === id);
      if (!sprint) return null;
      return {
        projectId: sprint.projectId,
        startDate: sprint.startDate,
        endDate: sprint.endDate,
      };
    })
    .filter(Boolean);
});

// Get assignee filter IDs
const assigneeIds = computed(() => {
  return filters.value[feedFilterTypes.assignee] ?? [];
});

// Planning data query - loads planned man-days for selected sprints
const { result: planningResult } = useQuery(
  GET_PLANNED_MAN_DAYS_FOR_SPRINTS,
  () => ({
    sprints: sprintConfigs.value,
    userIds: assigneeIds.value.length > 0 ? assigneeIds.value : undefined,
  }),
  () => ({
    enabled: sprintConfigs.value.length > 0,
  }),
);

// Transform planning result into a map by sprint ID
const sprintPlanningData = computed(() => {
  if (!planningResult.value?.plannedManDaysForSprints) return {};

  const data: Record<
    string,
    {
      plannedManDays: number;
      remainingPlannedManDays: number;
      users: Array<{
        userId: string;
        fullName: string;
        plannedManDays: number;
      }>;
    }
  > = {};

  planningResult.value.plannedManDaysForSprints.forEach(
    (planning: {
      projectId: string;
      startDate: string;
      endDate: string;
      plannedManDays: number;
      remainingPlannedManDays: number;
      users: Array<{
        userId: string;
        fullName: string;
        plannedManDays: number;
      }>;
    }) => {
      // Find the matching sprint
      const matchingSprint = sprints.value.find(
        (s: { projectId: string; startDate: string; endDate: string }) =>
          s.projectId === planning.projectId &&
          s.startDate === planning.startDate &&
          s.endDate === planning.endDate,
      );
      if (matchingSprint) {
        data[matchingSprint.id] = {
          plannedManDays: planning.plannedManDays,
          remainingPlannedManDays: planning.remainingPlannedManDays,
          users: planning.users ?? [],
        };
      }
    },
  );

  return data;
});

/*
 * Create Sprint
 */
const { mutate: createSprint, onError: onSprintCreatedError } =
  useMutation(CREATE_SPRINT);
onSprintCreatedError(createErrorHandler("createSprint"));

async function handleCreateSprint(payload: {
  projectId: string;
  label?: string;
  startDate: string;
  endDate: string;
}) {
  const result = await createSprint(payload);
  const sprint = result?.data?.createSprint?.data;

  if (!sprint || !activity.value?.id) return;

  await updateActivity({
    id: activity.value.id,
    sprintId: sprint.id,
  });

  // Refetch sprints for the project
  refetchSprints();

  showToast(t("activity.sprint.created"), "success");
}

/*
 * Fetch time spent for all displayed activities.
 */
const activityIdsForQuery = computed(() => {
  const ids = activities.value
    .map((activity: { id: string }) => activity.id)
    .filter(Boolean);

  return ids.length > 0 ? ids : undefined;
});

const activityTimeSpentQueryOptions = computed(() => ({
  enabled: Boolean(activityIdsForQuery.value),
}));

const { result: activityTimeSpentResult } = useQuery(
  GET_ACTIVITY_TIME_SPENT,
  () => ({
    activityIds: activityIdsForQuery.value,
  }),
  activityTimeSpentQueryOptions,
);

const timeTrackingData = computed(() => {
  const result = activityTimeSpentResult.value?.activityTimeSpent?.data ?? [];

  const map: Record<
    string,
    {
      timeSpent: number;
    }
  > = {};

  result.forEach((item: { activityId: string; timeSpent: number }) => {
    map[item.activityId] = {
      timeSpent: item.timeSpent,
    };
  });

  return map;
});

/*
 * Project users
 *
 * IMPORTANT:
 * useQuery() is created once during setup.
 * We enable/disable the existing query reactively rather
 * than creating a new composable inside computed().
 */
const { result: usersResult, onError: onUsersError } = useQuery(
  GET_PROJECT_USERS,
  () => ({
    projectId: route.params.id,
  }),
  () => ({
    enabled: isUserEmployee.value,
  }),
);

onUsersError(createErrorHandler("getProjectUsers"));

const users = computed(() => {
  if (!isUserEmployee.value) {
    return [];
  }

  return (usersResult.value?.project?.accessRights?.items ?? [])
    .map((ar: ProjectAccessRight) => ar.user)
    .filter((user: User) => user.firstName && user.lastName);
});

/*
 * Phases
 *
 * Same principle: one Apollo composable created
 * synchronously during setup.
 *
 * The variables remain reactive, so changing
 * includesInactive will refresh the query.
 */
const { result: phasesResult, onError: onPhasesError } = useQuery(
  GET_PHASES,
  () => ({
    projectId: route.params.id,
    includeInactive: filters.value.includesInactive,
  }),
);

onPhasesError(createErrorHandler("getPhases"));

const phases = computed(() => phasesResult.value?.phases ?? []);

/*
 * Milestones for the project (for filter dropdown)
 */
const { result: milestonesResult, onError: onMilestonesError } = useQuery(
  GET_MILESTONES_FOR_PROJECTS,
  () => ({
    projectIds: route.params.id ? [route.params.id] : [],
  }),
  () => ({
    enabled: !!route.params.id,
  }),
);

onMilestonesError(createErrorHandler("getMilestones"));

const milestones = computed(
  () => milestonesResult.value?.milestonesForProjects ?? [],
);

/*
 * Templates
 */
const { result: templatesResult, onError: onTemplatesNamesError } = useQuery(
  GET_TEMPLATES_NAMES,
  () => ({
    projectId: route.params.id,
  }),
  () => ({
    enabled: isUserEmployee.value,
  }),
);

onTemplatesNamesError(createErrorHandler("getTemplatesNames"));

const templates = computed(() => templatesResult.value?.templates ?? []);

/*
 * Activity statuses
 */
const { result: activityStatusResult, onError: onActivityStatusesError } =
  useQuery(GET_ACTIVITY_STATUSES);

const activityStatus = computed(() => {
  return activityStatusResult.value?.activityStatuses ?? [];
});

onActivityStatusesError(createErrorHandler("getActivityStatuses"));

const {
  result: activityDevStatusesResult,
  onError: onActivityDevStatusesError,
} = useQuery(GET_ACTIVITY_DEV_STATUSES);
const activityDevStatuses = computed(() => {
  return activityDevStatusesResult.value?.activityDevStatuses ?? [];
});
onActivityDevStatusesError(createErrorHandler("getActivityDevStatuses"));

/*
 * Selected activity
 *
 * Previously useQuery() was created inside watch(activityId).
 * That has the same lifecycle problem as useQuery() inside
 * computed().
 *
 * Create the Apollo query once and control it through enabled.
 */
const activityId = ref<string | null>(null);

const { result: selectedActivityResult, onError: onSelectedActivityError } =
  useQuery(
    GET_ACTIVITY,
    () => ({
      id: activityId.value ?? "",
    }),
    () => ({
      enabled: Boolean(activityId.value),
    }),
  );

onSelectedActivityError(createErrorHandler("getActivity"));

const activity = computed(() => {
  if (!activityId.value) {
    return undefined;
  }

  return selectedActivityResult.value?.activity;
});

/*
 * Activity History
 *
 * Lazy-loaded: no request is made until the History tab explicitly asks for it.
 *
 * Activities can be opened in two ways:
 * - Activity Feed sets activityId directly.
 * - Project Overview opens the modal through the route, so the selected activity
 *   id can exist only in route.params.activityId.
 *
 * Keep one source for the history record id so both entry points work.
 */
const historyRecordId = computed(() => {
  return (
    activity.value?.id ??
    activityId.value ??
    (route.params.activityId as string | undefined) ??
    null
  );
});

const {
  result: activityHistoryResult,
  loading: activityHistoryLoading,
  load: loadHistory,
  refetch: refetchHistory,
} = useLazyQuery(
  GET_HISTORY_EVENTS,
  () => ({
    tableName: "base_activities",
    recordId: historyRecordId.value ?? "",
    depth: 1,
  }),
  {
    fetchPolicy: "network-only",
  },
);

const activityHistory = computed(() => {
  return activityHistoryResult.value?.historyEvents ?? [];
});

function fetchHistory() {
  const recordId = historyRecordId.value;
  if (!recordId) return;

  loadHistory() ||
    refetchHistory({
      tableName: "base_activities",
      recordId,
      depth: 1,
    });
}

/*
 * Queries to refetch after mutations.
 */
const refetchProjectPhase = [
  {
    query: GET_PHASES,
    variables: {
      projectId: route.params.id,
      includeInactive: true,
    },
  },
  {
    query: GET_PHASES,
    variables: {
      projectId: route.params.id,
      includeInactive: false,
    },
  },
];

const refetchQueries: UseMutationOptions["refetchQueries"] = () => [
  ...refetchProjectPhase,
  "getFullActivities",
  "getBasicActivities",
  ...(activityId.value
    ? [
        {
          query: GET_ACTIVITY,
          variables: {
            id: activityId.value,
          },
        },
      ]
    : []),
];

/*
 * Activity mutations only need to refresh the data
 * used by ProjectOverview.
 *
 * Keep the currently visible phase tree in sync,
 * refresh full activities, and refresh the selected
 * activity detail only when one is open.
 */
const refetchActivityQueries: UseMutationOptions["refetchQueries"] = () => [
  {
    query: GET_PHASES,
    variables: {
      projectId: route.params.id,
      includeInactive: filters.value.includesInactive,
    },
  },
  "getFullActivities",
  ...(activityId.value
    ? [
        {
          query: GET_ACTIVITY,
          variables: {
            id: activityId.value,
          },
        },
      ]
    : []),
];

const closedStatus = computed(() => {
  if (!activityStatus.value) {
    return undefined;
  }

  return activityStatus.value[activityStatus.value.length - 1];
});

/*
 * Phase mutations
 */
const {
  mutate: addPhase,
  onError: onPhaseCreatedError,
  onDone: onPhaseCreated,
} = useMutation(CREATE_PHASE, {
  refetchQueries: refetchProjectPhase,
});

onPhaseCreatedError(createErrorHandler("createPhase"));

onPhaseCreated(createResultHandler("createPhase"));

const {
  mutate: updatePhase,
  onError: onPhaseUpdatedError,
  onDone: onPhaseUpdated,
} = useMutation(UPDATE_PHASE, {
  refetchQueries: refetchProjectPhase,
});

onPhaseUpdatedError(createErrorHandler("updatePhase"));

onPhaseUpdated(createResultHandler("updatePhase"));

const {
  mutate: archivePhase,
  onError: onPhaseArchivedError,
  onDone: onPhaseArchived,
} = useMutation(ARCHIVE_PHASE, {
  refetchQueries: refetchProjectPhase,
});

onPhaseArchivedError(createErrorHandler("archivePhase"));

onPhaseArchived(createResultHandler("archivePhase"));

const {
  mutate: unarchivePhase,
  onError: onPhaseUnarchivedError,
  onDone: onPhaseUnarchived,
} = useMutation(UNARCHIVE_PHASE, {
  refetchQueries: refetchProjectPhase,
});

onPhaseUnarchivedError(createErrorHandler("unarchivePhase"));

onPhaseUnarchived(createResultHandler("unarchivePhase"));

/*
 * Milestone mutations
 */
const {
  mutate: addMilestone,
  onDone: onMilestoneCreated,
  onError: onMilestoneCreatedError,
} = useMutation(CREATE_MILESTONE, {
  refetchQueries: refetchProjectPhase,
});

onMilestoneCreatedError(createErrorHandler("createMilestone"));

onMilestoneCreated(createResultHandler("createMilestone"));

const {
  mutate: updateMilestone,
  onDone: onMilestoneUpdated,
  onError: onMilestoneUpdatedError,
} = useMutation(UPDATE_MILESTONE, {
  refetchQueries: refetchProjectPhase,
});

onMilestoneUpdatedError(createErrorHandler("updateMilestone"));

onMilestoneUpdated(createResultHandler("updateMilestone"));

const {
  mutate: archiveMilestone,
  onDone: onMilestoneArchived,
  onError: onMilestoneArchivedError,
} = useMutation(ARCHIVE_MILESTONE, {
  refetchQueries: refetchProjectPhase,
});

onMilestoneArchivedError(createErrorHandler("archiveMilestone"));

onMilestoneArchived(createResultHandler("archiveMilestone"));

const {
  mutate: unarchiveMilestone,
  onDone: onMilestoneUnarchived,
  onError: onMilestoneUnarchivedError,
} = useMutation(UNARCHIVE_MILESTONE, {
  refetchQueries: refetchProjectPhase,
});

onMilestoneUnarchivedError(createErrorHandler("unarchiveMilestone"));

onMilestoneUnarchived(createResultHandler("unarchiveMilestone"));

/*
 * Bulk mutations (Phase / Milestone / Activity)
 *
 * Orcha owns the modal/loading state. Each payload includes onSuccess/onError
 * callbacks so Fides can explicitly resolve that state after the async mutation.
 */
const { mutate: bulkCreatePhasesMutation } = useMutation(BULK_CREATE_PHASES, {
  refetchQueries: refetchProjectPhase,
});

async function handleBulkAddPhase(payload: BulkAddPhasePayload) {
  try {
    const result = await bulkCreatePhasesMutation({
      projectId: payload.projectId,
      phases: payload.phases,
    });

    const response = result?.data?.bulkCreatePhases;
    if (response?.status !== "ok") {
      throw new Error(response?.error || "Bulk phase creation failed");
    }

    toast.success(
      t("project.bulk.created", {
        count: response.data?.length ?? payload.phases.length,
      }),
    );
    payload.onSuccess();
  } catch (error) {
    logErrorMessages(error);
    toast.error(
      t("errors.bulkCreatePhases", {
        message: error instanceof Error ? error.message : "",
      }),
    );
    payload.onError();
  }
}

const { mutate: bulkCreateMilestonesMutation } = useMutation(
  BULK_CREATE_MILESTONES,
  {
    refetchQueries: refetchProjectPhase,
  },
);

async function handleBulkAddMilestone(payload: BulkAddMilestonePayload) {
  try {
    const result = await bulkCreateMilestonesMutation({
      phaseId: payload.phaseId,
      milestones: payload.milestones,
    });

    const response = result?.data?.bulkCreateMilestones;
    if (response?.status !== "ok") {
      throw new Error(response?.error || "Bulk milestone creation failed");
    }

    toast.success(
      t("project.bulk.created", {
        count: response.data?.length ?? payload.milestones.length,
      }),
    );
    payload.onSuccess();
  } catch (error) {
    logErrorMessages(error);
    toast.error(
      t("errors.bulkCreateMilestones", {
        message: error instanceof Error ? error.message : "",
      }),
    );
    payload.onError();
  }
}

const { mutate: bulkCreateActivitiesMutation } = useMutation(
  BULK_CREATE_ACTIVITIES,
  {
    refetchQueries: refetchActivityQueries,
  },
);

async function handleBulkAddActivity(payload: BulkAddActivityPayload) {
  try {
    const variables = await buildActivityVariables({
      milestoneId: payload.milestoneId,
      projectId: payload.projectId,
      activities: payload.activities,
    });

    const result = await bulkCreateActivitiesMutation(variables);
    const response = result?.data?.bulkCreateActivities;

    if (response?.status !== "ok") {
      throw new Error(response?.error || "Bulk activity creation failed");
    }

    toast.success(
      t("project.bulk.created", {
        count: response.data?.length ?? payload.activities.length,
      }),
    );
    payload.onSuccess();
  } catch (error) {
    logErrorMessages(error);
    toast.error(
      t("errors.bulkCreateActivities", {
        message: error instanceof Error ? error.message : "",
      }),
    );
    payload.onError();
  }
}

/*
 * Activity mutations
 */
async function buildActivityVariables(payload: object) {
  const clientPrefix = projectResult.value?.project.client.prefix;

  return {
    ...payload,
    ...(clientPrefix
      ? {
          slugPrefix: clientPrefix,
        }
      : {}),
  };
}

const {
  mutate: createActivity,
  onDone: onActivityCreated,
  onError: onActivityCreatedError,
} = useMutation(CREATE_ACTIVITY, {
  refetchQueries: refetchActivityQueries,
});

onActivityCreatedError(createErrorHandler("createActivity"));

onActivityCreated(createResultHandler("createActivity"));

async function handleCreateActivity(payload: object) {
  const variables = await buildActivityVariables(payload);

  await createActivity(variables);
}

const {
  mutate: updateActivity,
  onDone: onActivityupdated,
  onError: onActivityupdatedError,
} = useMutation(UPDATE_ACTIVITY, {
  refetchQueries: refetchActivityQueries,
});

onActivityupdatedError(createErrorHandler("updateActivity"));

onActivityupdated(createResultHandler("updateActivity"));

async function handleUpdateActivity(payload: object) {
  const variables = await buildActivityVariables(payload);

  await updateActivity(variables);
}

const {
  mutate: archiveActivity,
  onDone: onActivityArchived,
  onError: onActivityArchivedError,
} = useMutation(ARCHIVE_ACTIVITY, {
  refetchQueries: refetchActivityQueries,
});

onActivityArchivedError(createErrorHandler("archiveActivity"));

onActivityArchived(createResultHandler("archiveActivity"));

const {
  mutate: unarchiveActivity,
  onDone: onActivityUnarchived,
  onError: onActivityUnarchivedError,
} = useMutation(UNARCHIVE_ACTIVITY, {
  refetchQueries: refetchActivityQueries,
});

onActivityUnarchivedError(createErrorHandler("unarchiveActivity"));

onActivityUnarchived(createResultHandler("unarchiveActivity"));

/*
 * Tasks
 */
const {
  mutate: addTask,
  onDone: onTaskcreated,
  onError: onTaskcreatedError,
} = useMutation(CREATE_TASK, {
  refetchQueries,

  update: (cache, { data: { createTask } }) => {
    if (createTask?.data) {
      return updateQueryCache(
        cache,
        createTask.data,
        ActivityTasksCacheInfoHolder,
        insertNewEntity,
        activityId.value as string,
      );
    }
  },
});

onTaskcreatedError(createErrorHandler("createTask"));

onTaskcreated(createResultHandler("createTask"));

const {
  mutate: updateTask,
  onDone: onTaskUpdated,
  onError: onTaskUpdatedError,
} = useMutation(UPDATE_TASK, {
  refetchQueries,
});

onTaskUpdatedError(createErrorHandler("updateTask"));

onTaskUpdated(createResultHandler("updateTask"));

const {
  mutate: deleteTask,
  onError: onTaskDeletedError,
  onDone: onTaskDeleted,
} = useMutation(DELETE_TASK, {
  refetchQueries,

  update: (cache, { data: { deleteTask } }) => {
    if (deleteTask?.data) {
      return updateQueryCache(
        cache,
        deleteTask.data,
        ActivityTasksCacheInfoHolder,
        removeEntity,
        activityId.value as string,
      );
    }
  },
});

onTaskDeletedError(createErrorHandler("deleteTask"));

onTaskDeleted(createResultHandler("deleteTask"));

/*
 * Attachments
 */
const {
  mutate: createAttachment,
  onDone: onAttachmentCreated,
  onError: onAttachmentCreatedError,
} = useMutation(CREATE_ATTACHMENT, {
  refetchQueries,

  update: (cache, { data: { createAttachment } }) => {
    if (createAttachment?.data) {
      return updateQueryCache(
        cache,
        createAttachment.data,
        ActivityAttachmentsCacheInfoHolder,
        insertNewEntity,
        activityId.value as string,
      );
    }
  },
});

onAttachmentCreatedError(createErrorHandler("createAttachment"));

onAttachmentCreated(createResultHandler("createAttachment"));

const addFiles = async ({ activityId, files }: AddFilesPayload) => {
  try {
    const result = await uploadFile(files);

    await Promise.all(
      result.map((fileId) =>
        createAttachment({
          activityId,
          fileId,
        }),
      ),
    );
  } catch (error) {
    logErrorMessages(error);
    toast.error(t("errors.uploadingFile"));
  }
};

const loadAttachmentPreview = async (attachment: Attachment): Promise<Blob> => {
  if (!attachment.url) {
    throw new Error("Attachment URL is missing");
  }

  return getFileBlob(attachment.url);
};

const {
  mutate: deleteAttachment,
  onDone: onAttachmentDeleted,
  onError: onAttachmentDeletedError,
} = useMutation(DELETE_ATTACHMENT, {
  refetchQueries,

  update: (cache, { data: { deleteAttachment } }) => {
    if (deleteAttachment?.data) {
      return updateQueryCache(
        cache,
        deleteAttachment.data,
        ActivityAttachmentsCacheInfoHolder,
        removeEntity,
        activityId.value as string,
      );
    }
  },
});

onAttachmentDeletedError(createErrorHandler("deleteAttachment"));

onAttachmentDeleted(createResultHandler("deleteAttachment"));

const removeFile = (payload: RemoveFilePayload) => {
  deleteAttachment({
    id: payload.file.id,
  });
};

/*
 * Mention notifications
 */
const {
  mutate: notifyMentionedUsers,
  onError: onNotifyMentionedUsersError,
  onDone: onNotifyMentionedUsers,
} = useMutation(ON_NOTIFY_MENTIONED_USERS, {});

onNotifyMentionedUsersError(createErrorHandler("notifyMentionedUsers"));

onNotifyMentionedUsers(createResultHandler("notifyMentionedUsers"));

/*
 * Comments
 */
const {
  mutate: addComment,
  onDone: onCommentCreated,
  onError: onCommentCreatedError,
} = useMutation(CREATE_COMMENT, {
  refetchQueries,

  update: (cache, { data: { createComment } }) => {
    if (createComment?.data) {
      return updateQueryCache(
        cache,
        createComment.data,
        ActivityCommentsCacheInfoHolder,
        insertNewEntity,
        activityId.value as string,
      );
    }
  },
});

onCommentCreatedError(createErrorHandler("createComment"));

onCommentCreated(createResultHandler("createComment"));

const handleCreateComment = async (payload: AddCommentPayload) => {
  const { activityId, authorId, message, mentionedUserIds } = payload;

  const result = await addComment({
    activityId,
    authorId,
    message,
  });

  if (result?.data?.createComment?.status === "ok") {
    if (mentionedUserIds.length === 0) {
      return;
    }

    const activityRoute = router.resolve({
      name: "ActivityFeed",
      params: {
        id: activityId,
      },
    });

    const fullLink = window.location.origin + activityRoute.href;

    await notifyMentionedUsers({
      userIds: mentionedUserIds,
      comment: result.data.createComment.data.message,
      activityName: activity.value.name,
      activityLink: fullLink,
      authorId,
    });
  }
};

const {
  mutate: updateComment,
  onDone: onCommentUpdated,
  onError: onCommentUpdatedError,
} = useMutation(UPDATE_COMMENT, {
  refetchQueries,
});

onCommentUpdatedError(createErrorHandler("updateComment"));

onCommentUpdated(createResultHandler("updateComment"));

const handleUpdateComment = async (payload: UpdateCommentPayload) => {
  const { id, message, mentionedUserIds } = payload;

  const result = await updateComment({
    id,
    message,
  });

  if (result?.data?.updateComment?.status === "ok") {
    if (mentionedUserIds.length === 0) {
      return;
    }

    const activityRoute = router.resolve({
      name: "ActivityFeed",
      params: {
        id: activityId.value,
      },
    });

    const fullLink = window.location.origin + activityRoute.href;

    await notifyMentionedUsers({
      userIds: mentionedUserIds,
      comment: result.data.updateComment.data.message,
      activityName: activity.value.name,
      activityLink: fullLink,
      authorId: result.data.updateComment.data.author.id,
    });
  }
};

const {
  mutate: removeComment,
  onDone: onCommentDeleted,
  onError: onCommentDeletedError,
} = useMutation(DELETE_COMMENT, {
  refetchQueries,

  update: (cache, { data: { deleteComment } }) => {
    if (deleteComment?.data) {
      return updateQueryCache(
        cache,
        deleteComment.data,
        ActivityCommentsCacheInfoHolder,
        removeEntity,
        activityId.value as string,
      );
    }
  },
});

onCommentDeletedError(createErrorHandler("deleteComment"));

onCommentDeleted(createResultHandler("deleteComment"));

/*
 * Templates
 */
const {
  mutate: applyTemplate,
  onDone: onTemplateApplied,
  onError: onTemplateAppliedError,
} = useMutation(APPLY_TEMPLATE, {
  refetchQueries,
});

onTemplateAppliedError(createErrorHandler("applyTemplate"));

onTemplateApplied(createResultHandler("applyTemplate"));

const {
  mutate: saveTemplate,
  onDone: onTemplateSaved,
  onError: onTemplateSavedError,
} = useMutation(SAVE_TEMPLATE);

onTemplateSavedError(() => {
  createErrorHandler("saveTemplate");

  toast.error(t("project.template.save.error"));
});

onTemplateSaved(() => {
  createResultHandler("saveTemplate");

  toast.success(t("project.template.save.success"));
});

/*
 * Activity selection
 */
const selectActivity = async (givenActivityId: string | null) => {
  activityId.value = givenActivityId;
};

function showToast(messageKey: string, type: string) {
  if (type === "success") {
    toast.success(t(messageKey));
  } else {
    toast.error(t(messageKey));
  }
}

/*
 * Handle startTimer event from Orcha components
 * via postMessage.
 *
 * AoProjectOverviewMilestoneDetails, AoCardFeed, etc.
 * send postMessage when the timer button is clicked.
 */
const handlePostMessage = (event: MessageEvent) => {
  const { type, data } = event.data || {};

  if (type === "startTimer") {
    /*
     * Support both old format
     * (name, slug, projectId)
     * and new format
     * (activityId, activityName, etc.)
     */
    const activityId = data.activityId;

    const activityName = data.activityName || data.name;

    const projectId = data.projectId;

    const project = data.project;

    const activitySlug = data.activitySlug || data.slug;

    console.log("[ProjectOverview] Received startTimer postMessage:", {
      activityId,
      activityName,
      projectId,
      project,
      activitySlug,
    });

    /*
     * Dispatch custom event with full payload
     * for FloatingTimer.
     */
    window.dispatchEvent(
      new CustomEvent("fides:startTimer", {
        detail: {
          activityId,
          activityName,
          projectId,
          project,
          activitySlug,
        },
      }),
    );
  }
};

onMounted(() => {
  window.addEventListener("message", handlePostMessage);
});

onUnmounted(() => {
  window.removeEventListener("message", handlePostMessage);
});

return (_ctx: any,_cache: any) => {
  const _component_RouterView = _resolveComponent("RouterView")!

  return (_unref(projectResult))
    ? (_openBlock(), _createBlock(_component_RouterView, {
        key: 0,
        "search-text": filters.value.searchTerm,
        "onUpdate:searchText": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.searchTerm) = $event)),
        filters: filters.value,
        "onUpdate:filters": _cache[1] || (_cache[1] = ($event: any) => ((filters).value = $event)),
        "shows-archived-activities": filters.value.includesInactive,
        "onUpdate:showsArchivedActivities": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.includesInactive) = $event)),
        "shows-only-important": filters.value.onlyImportant,
        "onUpdate:showsOnlyImportant": _cache[3] || (_cache[3] = ($event: any) => ((filters.value.onlyImportant) = $event)),
        "shows-archived": filters.value.includesInactive,
        "onUpdate:showsArchived": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.includesInactive) = $event)),
        "shows-only-non-assigned": filters.value.onlyNonAssigned,
        "onUpdate:showsOnlyNonAssigned": _cache[5] || (_cache[5] = ($event: any) => ((filters.value.onlyNonAssigned) = $event)),
        activities: activities.value,
        sprints: sprints.value,
        "activity-sprints": sprints.value,
        "sprint-planning-data": sprintPlanningData.value,
        "time-tracking-data": timeTrackingData.value,
        "onClear:searchText": _cache[6] || (_cache[6] = ($event: any) => (filters.value.searchTerm = '')),
        project: _unref(projectResult).project,
        phases: phases.value,
        milestones: milestones.value,
        users: users.value,
        "user-id": _unref(userResult)?.user?.id,
        "is-task-checkable": () => true,
        templates: templates.value,
        statuses: activityStatus.value,
        "dev-statuses": activityDevStatuses.value,
        "is-editable": isUserEmployee.value ? true : 'only-own',
        "closed-status": closedStatus.value,
        "selected-activity": activity.value,
        "attachment-enabled": true,
        "load-attachment-preview": loadAttachmentPreview,
        history: activityHistory.value,
        "history-loading": _unref(activityHistoryLoading),
        onSelectActivity: selectActivity,
        onAddPhase: _unref(addPhase),
        onBulkAddPhase: handleBulkAddPhase,
        onUpdatePhase: _unref(updatePhase),
        onArchivePhase: _unref(archivePhase),
        onUnarchivePhase: _unref(unarchivePhase),
        onAddMilestone: _unref(addMilestone),
        onBulkAddMilestone: handleBulkAddMilestone,
        onUpdateMilestone: _unref(updateMilestone),
        onArchiveMilestone: _unref(archiveMilestone),
        onUnarchiveMilestone: _unref(unarchiveMilestone),
        onAddActivity: handleCreateActivity,
        onBulkAddActivity: handleBulkAddActivity,
        "onUpdate:activity": handleUpdateActivity,
        onArchiveActivity: _unref(archiveActivity),
        onUnarchiveActivity: _unref(unarchiveActivity),
        onAddSubtask: _unref(addTask),
        onUpdateSubtask: _unref(updateTask),
        onDeleteSubtask: _unref(deleteTask),
        onAddFiles: addFiles,
        onRemoveFile: removeFile,
        onAddComment: handleCreateComment,
        onUpdateComment: handleUpdateComment,
        onRemoveComment: _unref(removeComment),
        onApplyTemplate: _unref(applyTemplate),
        onSaveTemplate: _unref(saveTemplate),
        onToast: showToast,
        onStartTimer: _ctx.emitStartTimer,
        onCreateSprint: handleCreateSprint,
        onFetchHistory: fetchHistory
      }, null, 8, ["search-text", "filters", "shows-archived-activities", "shows-only-important", "shows-archived", "shows-only-non-assigned", "activities", "sprints", "activity-sprints", "sprint-planning-data", "time-tracking-data", "project", "phases", "milestones", "users", "user-id", "templates", "statuses", "dev-statuses", "is-editable", "closed-status", "selected-activity", "history", "history-loading", "onAddPhase", "onUpdatePhase", "onArchivePhase", "onUnarchivePhase", "onAddMilestone", "onUpdateMilestone", "onArchiveMilestone", "onUnarchiveMilestone", "onArchiveActivity", "onUnarchiveActivity", "onAddSubtask", "onUpdateSubtask", "onDeleteSubtask", "onRemoveComment", "onApplyTemplate", "onSaveTemplate", "onStartTimer"]))
    : _createCommentVNode("", true)
}
}

})